import React from 'react';
import { Link, router } from '@inertiajs/react';
import BlogLayout from '@/components/admin/BlogLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal, Plus, Pencil, Trash2, Tag } from 'lucide-react';

interface Tag {
  id: number;
  name: string;
  slug: string;
  post_count: number;
  created_at: string;
  updated_at: string;
}

interface TagListProps {
  tags: Tag[];
  canCreate?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
}

const TagList: React.FC<TagListProps> = ({ 
  tags, 
  canCreate = true, 
  canEdit = true, 
  canDelete = false 
}) => {
  const handleDelete = (tagId: number) => {
    if (confirm('Are you sure you want to delete this tag?')) {
      router.delete(`/admin/blog/tags/${tagId}`);
    }
  };

  const breadcrumbs = [
    { label: 'Admin', href: '/admin' },
    { label: 'Blog', href: '/admin/blog/posts' },
    { label: 'Tags' },
  ];

  return (
    <BlogLayout breadcrumbs={breadcrumbs}>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Tags</h1>
            <p className="text-muted-foreground">Manage your blog tags</p>
          </div>
          {canCreate && (
            <Button asChild>
              <Link href="/admin/blog/tags/new">
                <Plus className="mr-2 h-4 w-4" />
                New Tag
              </Link>
            </Button>
          )}
        </div>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Posts</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tags.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="h-24 text-center">
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <Tag className="h-8 w-8" />
                      <p>No tags yet</p>
                      {canCreate && (
                        <Button variant="outline" size="sm" asChild>
                          <Link href="/admin/blog/tags/new">
                            Create your first tag
                          </Link>
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                tags.map((tag) => (
                  <TableRow key={tag.id}>
                    <TableCell>
                      <div className="font-medium">{tag.name}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{tag.slug}</Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{tag.post_count}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {canEdit && (
                            <DropdownMenuItem asChild>
                              <Link href={`/admin/blog/tags/${tag.id}/edit`}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Edit
                              </Link>
                            </DropdownMenuItem>
                          )}
                          {canDelete && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleDelete(tag.id)}
                                className="text-red-600"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </BlogLayout>
  );
};

export default TagList;
